/**
 * Sales Order Progress — produced / packed / dispatched / balance per SO.
 *
 * Route: /production/sales-order-progress
 *
 * Spec PROD-001 Section 30. Reads /production/sales-order-progress and
 * shows a row per active sales order with delivery date, delay days,
 * production status. The Refresh button on each row force-recomputes the
 * SO's counters from current logs + FG lots.
 */
import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Button, Card, Col, Input, Row, Select, Space, Statistic, Switch, Table,
  Tag, Tooltip, Typography, message,
} from 'antd'
import {
  CheckCircleOutlined, ClockCircleOutlined, ReloadOutlined,
  WarningOutlined,
} from '@ant-design/icons'
import dayjs from 'dayjs'

import {
  PRODUCTION_TYPE_COLOR, productionSpineApi,
  type SOProgressRow,
} from '@/features/production/productionSpineApi'
import { usePlantId } from '@/shared/hooks/usePlantId'

const { Title, Text } = Typography

export default function SalesOrderProgressPage() {
  const qc = useQueryClient()
  const { plantId } = usePlantId()
  const [delayedOnly, setDelayedOnly] = useState(false)
  const [search, setSearch] = useState('')

  const listQ = useQuery({
    enabled: !!plantId,
    queryKey: ['so-progress', plantId, delayedOnly],
    queryFn: () => productionSpineApi.listSOProgress({
      plant_id: plantId!,
      delayed_only: delayedOnly || undefined,
    }),
    refetchInterval: 60_000,
  })

  const recomputeMut = useMutation({
    mutationFn: (sales_order_id: string) => productionSpineApi.recomputeSO(sales_order_id),
    onSuccess: () => {
      message.success('Counters refreshed')
      qc.invalidateQueries({ queryKey: ['so-progress'] })
    },
    onError: () => message.error('Recompute failed'),
  })

  const rows = useMemo(() => {
    const all = listQ.data ?? []
    if (!search.trim()) return all
    const s = search.toLowerCase()
    return all.filter((r) =>
      (r.so_no || '').toLowerCase().includes(s) ||
      (r.customer_name || '').toLowerCase().includes(s) ||
      (r.count_description || '').toLowerCase().includes(s),
    )
  }, [listQ.data, search])

  // Aggregate KPIs across the table view
  const totals = useMemo(() => {
    const out = {
      ordered: 0, produced: 0, packed: 0, dispatched: 0,
      pending: 0, delayed: 0,
    }
    for (const r of rows) {
      out.ordered += r.ordered_qty_kg
      out.produced += r.produced_qty_kg
      out.packed += r.packed_qty_kg
      out.dispatched += r.dispatched_qty_kg
      out.pending += r.pending_production_kg
      if (r.delay_days && r.delay_days > 0) out.delayed += 1
    }
    return out
  }, [rows])

  return (
    <div style={{ padding: 24 }}>
      <Title level={3} style={{ marginBottom: 4 }}>Sales Order Progress</Title>
      <Text type="secondary">
        Produced / packed / dispatched per active sales order. Counters update
        automatically when production logs are APPROVED and when FG lots are
        created. Use the refresh icon on a row to force-recompute.
      </Text>

      {/* KPI strip */}
      <Row gutter={16} style={{ marginTop: 16, marginBottom: 16 }}>
        <Col xs={12} sm={8} md={4}>
          <Card size="small">
            <Statistic title="Orders in view" value={rows.length} />
          </Card>
        </Col>
        <Col xs={12} sm={8} md={4}>
          <Card size="small">
            <Statistic
              title="Delayed"
              value={totals.delayed}
              valueStyle={{ color: totals.delayed > 0 ? '#cf1322' : '#000' }}
              prefix={totals.delayed > 0 ? <WarningOutlined /> : <CheckCircleOutlined />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={8} md={4}>
          <Card size="small">
            <Statistic
              title="Ordered (kg)"
              value={totals.ordered.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
            />
          </Card>
        </Col>
        <Col xs={12} sm={8} md={4}>
          <Card size="small">
            <Statistic
              title="Produced (kg)"
              value={totals.produced.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
            />
          </Card>
        </Col>
        <Col xs={12} sm={8} md={4}>
          <Card size="small">
            <Statistic
              title="Packed (kg)"
              value={totals.packed.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
            />
          </Card>
        </Col>
        <Col xs={12} sm={8} md={4}>
          <Card size="small">
            <Statistic
              title="Pending prod. (kg)"
              value={totals.pending.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
              valueStyle={{ color: '#fa8c16' }}
            />
          </Card>
        </Col>
      </Row>

      <Card size="small" style={{ marginBottom: 16 }}>
        <Space wrap>
          <Text>Search:</Text>
          <Input
            allowClear
            style={{ width: 280 }}
            placeholder="SO no / customer / count"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <Space size={4}>
            <Switch
              checked={delayedOnly}
              onChange={setDelayedOnly}
              size="small"
            />
            <Text>Delayed only</Text>
          </Space>
          <Button
            icon={<ReloadOutlined />}
            onClick={() => listQ.refetch()}
            loading={listQ.isFetching}
          >
            Refresh
          </Button>
        </Space>
      </Card>

      <Table<SOProgressRow>
        size="small"
        rowKey="sales_order_id"
        loading={listQ.isLoading}
        dataSource={rows}
        pagination={{ pageSize: 50, showSizeChanger: true }}
        scroll={{ x: 'max-content' }}
        columns={[
          {
            title: 'SO No',
            dataIndex: 'so_no',
            key: 'so_no',
            fixed: 'left',
            width: 140,
            render: (v: string) => <Text strong>{v}</Text>,
          },
          {
            title: 'Customer',
            dataIndex: 'customer_name',
            key: 'customer_name',
            width: 180,
            ellipsis: true,
          },
          {
            title: 'Count',
            dataIndex: 'count_description',
            key: 'count_description',
            width: 120,
          },
          {
            title: 'Type',
            dataIndex: 'production_type',
            key: 'production_type',
            width: 80,
            render: (v: string) => v ? (
              <Tag color={PRODUCTION_TYPE_COLOR[v as keyof typeof PRODUCTION_TYPE_COLOR] || 'default'}>
                {v.replace('MTS_', '').replace('MTO', 'MTO').slice(0, 3)}
              </Tag>
            ) : '-',
          },
          {
            title: 'Ordered',
            dataIndex: 'ordered_qty_kg',
            key: 'ordered_qty_kg',
            align: 'right',
            width: 100,
            render: (v: number) => v.toLocaleString('en-IN'),
          },
          {
            title: 'Produced',
            dataIndex: 'produced_qty_kg',
            key: 'produced_qty_kg',
            align: 'right',
            width: 100,
            render: (v: number) => v.toLocaleString('en-IN'),
          },
          {
            title: 'Packed',
            dataIndex: 'packed_qty_kg',
            key: 'packed_qty_kg',
            align: 'right',
            width: 100,
            render: (v: number) => v.toLocaleString('en-IN'),
          },
          {
            title: 'Dispatched',
            dataIndex: 'dispatched_qty_kg',
            key: 'dispatched_qty_kg',
            align: 'right',
            width: 110,
            render: (v: number) => v.toLocaleString('en-IN'),
          },
          {
            title: 'Pending Prod',
            dataIndex: 'pending_production_kg',
            key: 'pending_production_kg',
            align: 'right',
            width: 120,
            render: (v: number) =>
              <Text type={v > 0 ? 'warning' : 'success'}>
                {v.toLocaleString('en-IN')}
              </Text>,
          },
          {
            title: 'Delivery',
            dataIndex: 'delivery_date',
            key: 'delivery_date',
            width: 110,
            render: (v: string | null) => v ? dayjs(v).format('DD MMM YY') : '-',
          },
          {
            title: 'Delay',
            dataIndex: 'delay_days',
            key: 'delay_days',
            width: 80,
            render: (v: number | null) => v && v > 0 ? (
              <Tag color="red" icon={<ClockCircleOutlined />}>
                {v}d
              </Tag>
            ) : null,
          },
          {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            width: 100,
            render: (v: string) => <Tag>{v}</Tag>,
          },
          {
            title: '',
            key: 'action',
            fixed: 'right',
            width: 50,
            render: (_, r) => (
              <Tooltip title="Recompute from current data">
                <Button
                  size="small"
                  type="text"
                  icon={<ReloadOutlined />}
                  loading={recomputeMut.isPending}
                  onClick={() => recomputeMut.mutate(r.sales_order_id)}
                />
              </Tooltip>
            ),
          },
        ]}
      />
    </div>
  )
}
